import React, { useState } from 'react';
import { Modal, Button, Form, Spinner, Alert } from 'react-bootstrap';
import { requestWithAuthorization } from '../../../network/apiRequests';
import { auth } from '../../../firebase';

interface DownloadDataModalProps {
    show: boolean;
    onHide: () => void;
    recordingId: string;
    recordingName: string;
    sensorIds: string[];
    startTime: string;
    endTime: string;
    hasThirdPartyData?: boolean | null;
}

export default function DownloadDataModal({
    show,
    onHide,
    recordingId,
    recordingName,
    sensorIds,
    startTime,
    endTime,
    hasThirdPartyData = false
}: DownloadDataModalProps) {
    const [selectedSensorIds, setSelectedSensorIds] = useState<string[]>(sensorIds);
    const [dataType, setDataType] = useState<'processed' | 'fit-sync'>('processed');
    const [isDownloading, setIsDownloading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Synchronize selected sensors when sensorIds prop updates or modal is shown
    React.useEffect(() => {
        setSelectedSensorIds(sensorIds);
        setDataType('processed');
    }, [sensorIds, show]);

    const handleToggleSensor = (id: string) => {
        setSelectedSensorIds(prev =>
            prev.includes(id) ? prev.filter(sId => sId !== id) : [...prev, id]
        );
    };

    const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            setSelectedSensorIds(sensorIds);
        } else {
            setSelectedSensorIds([]);
        }
    };

    const handleDownload = async () => {
        if (selectedSensorIds.length === 0) {
            setError("Please select at least one sensor.");
            return;
        }

        setIsDownloading(true);
        setError(null);

        try {
            const columnsParam = (hasThirdPartyData && dataType === 'fit-sync') ? 'fit-sync' : 'processed';

            for (const sensorId of selectedSensorIds) {
                const url = `${process.env.REACT_APP_API_HOSTNAME}/recordings/sensor/${sensorId}/download?start=${encodeURIComponent(startTime)}&end=${encodeURIComponent(endTime)}&format=csv&columns=${columnsParam}&isZip=true`;

                const response = await requestWithAuthorization(url, { responseType: 'blob' }, 'get');

                if (response.status === 200) {
                    const blob = new Blob([response.data], { type: 'application/zip' });
                    const downloadUrl = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = downloadUrl;

                    let fileNameSuffix = '';
                    if (hasThirdPartyData && dataType === 'fit-sync') {
                        fileNameSuffix = '_fit-sync';
                    }

                    const fileName = selectedSensorIds.length > 1
                        ? `${recordingName}_${sensorId}${fileNameSuffix}.zip`
                        : `${recordingName}${fileNameSuffix}.zip`;

                    link.setAttribute('download', fileName);
                    document.body.appendChild(link);
                    link.click();
                    link.parentNode?.removeChild(link);
                    window.URL.revokeObjectURL(downloadUrl);
                } else {
                    setError(`Failed to download data for sensor ${sensorId}. Please try again.`);
                    break;
                }
            }
            onHide();
        } catch (err) {
            console.error("Download error:", err);
            setError("An error occurred while downloading the data.");
        } finally {
            setIsDownloading(false);
        }
    };

    const isAllSelected = sensorIds.length > 0 && selectedSensorIds.length === sensorIds.length;

    return (
        <Modal show={show} onHide={onHide} centered size="sm">
            <Modal.Header closeButton className="bg-dark text-white border-secondary">
                <Modal.Title>Download Data</Modal.Title>
            </Modal.Header>
            <Modal.Body className="bg-dark text-white p-4">
                {error && <Alert variant="danger" className="py-2 small">{error}</Alert>}

                {hasThirdPartyData && (
                    <Form.Group controlId="dataTypeSelect" className="mb-4">
                        <Form.Label className="text-muted small mb-2 fw-bold text-uppercase">Data Type</Form.Label>
                        <div>
                            <Form.Check
                                type="radio"
                                id="data-type-processed"
                                label="Normal Processed Data"
                                name="dataTypeOptions"
                                checked={dataType === 'processed'}
                                onChange={() => setDataType('processed')}
                                className="mb-2"
                            />
                            <Form.Check
                                type="radio"
                                id="data-type-fit-synced"
                                label="FIT-Synced Data"
                                name="dataTypeOptions"
                                checked={dataType === 'fit-sync'}
                                onChange={() => setDataType('fit-sync')}
                                className="mb-2"
                            />
                        </div>
                    </Form.Group>
                )}

                <Form.Group controlId="sensorSelect" className="mb-4">
                    <Form.Label className="text-muted small mb-2 fw-bold text-uppercase">Select Sensors</Form.Label>
                    {sensorIds.length > 1 && (
                        <div className="mb-2 pb-2 border-bottom border-secondary">
                            <Form.Check
                                type="checkbox"
                                id="select-all-sensors"
                                label={<span className="fw-bold">Select All</span>}
                                checked={isAllSelected}
                                onChange={handleSelectAll}
                            />
                        </div>
                    )}
                    <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        {sensorIds.map(id => (
                            <Form.Check
                                key={id}
                                type="checkbox"
                                id={`sensor-checkbox-${id}`}
                                label={id}
                                checked={selectedSensorIds.includes(id)}
                                onChange={() => handleToggleSensor(id)}
                                className="mb-2"
                            />
                        ))}
                    </div>
                </Form.Group>

                <div className="d-grid pt-2">
                    <Button
                        variant="primary"
                        onClick={handleDownload}
                        disabled={isDownloading || sensorIds.length === 0 || selectedSensorIds.length === 0}
                    >
                        {isDownloading ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Processing...
                            </>
                        ) : (
                            "Download ZIP"
                        )}
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
}
